import { apiConfig } from "~/configs/apiConfig";
import * as Sentry from "@sentry/remix";
import { ApiError } from "./ApiError";

interface RequestConfig {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  data?: any;
}

const IGNORE_ERROR_FOR_STATUSES: number[] = [404, 401];

const request = async <T>(config: RequestConfig): Promise<T> => {
  const headers: Record<string, string> = {
    ...config.headers,
    "Content-Type": "application/json",
  };

  // In Remix with Vite, we can use import.meta.env.SSR to detect server environment
  const baseUrl = import.meta.env.SSR
    ? apiConfig.ServerApiUrl
    : apiConfig.ClientApiUrl;

  let response;
  try {
    response = await fetch(`${baseUrl}${config.url}`, {
      method: config.method,
      headers,
      body: config.data ? JSON.stringify(config.data) : undefined,
    });
  } catch (e: any) {
    // Function name sets event name in sentry
    Sentry.withScope(function UnhandledApiErr(scope) {
      scope.setLevel("warning");
      scope.setTag("api_error", true);
      scope.setTag("status", e.status);
      scope.captureException(e);
    });
    throw e;
  }

  if (!response.ok) {
    if (IGNORE_ERROR_FOR_STATUSES.includes(response.status)) {
      throw response;
    }

    // Read full body so large JSON error payloads are not truncated before parse
    const errorText = await response.text();

    // Try parse the response body as json, otherwise use some defaults for error information
    let errorType: string;
    let userMessage: string | null | undefined;
    let info: unknown;
    try {
      const parsed = JSON.parse(errorText);
      errorType = parsed.errorType ?? "INTERNAL_SERVER_ERROR";
      userMessage = parsed.userMessage ?? null;
      info = parsed.info ?? null;
    } catch {
      // Non-JSON body (e.g. HTML 502 page) — don't put it in the error itself
      errorType = "INTERNAL_SERVER_ERROR";
      userMessage = undefined;
      info = null;
    }

    const apiError = new ApiError({
      errorType,
      status: response.status,
      userMessage,
      info,
    });

    // Function name sets event name in sentry
    Sentry.withScope(function UnexpectedApiError(scope) {
      scope.setLevel("warning");
      scope.setTag("api_error", true);
      scope.setTag("status", response.status);
      scope.setTag("error_type", apiError.errorType);
      // Cap the raw body snippet for debuggability without leaking it into the thrown error
      scope.setTag(
        "response_body_snippet",
        errorText.slice(0, 500).replace(/\s+/g, " "),
      );
      scope.setContext("Parsed Error", {
        errorType: apiError.errorType,
        status: apiError.status,
        userMessage: apiError.userMessage,
      });
      scope.captureException(apiError);
    });

    // Throw the API error
    throw apiError;
  }

  const hasBody =
    response.headers.get("content-length") !== "0" &&
    response.headers.get("content-type")?.includes("application/json");

  const data = hasBody ? await response.json() : null;
  return data ? data.data : data;
};

const instance = {
  get: <T>(
    url: string,
    config: Omit<RequestConfig, "url" | "method"> = {},
  ): Promise<T> => {
    return request({ url, method: "GET", ...config });
  },

  post: <T>(
    url: string,
    data: any,
    config: Omit<RequestConfig, "url" | "method" | "data"> = {},
  ): Promise<T> => {
    return request({ url, method: "POST", data, ...config });
  },

  put: <T>(
    url: string,
    data: any,
    config: Omit<RequestConfig, "url" | "method" | "data"> = {},
  ): Promise<T> => {
    return request({ url, method: "PUT", data, ...config });
  },
};

export default instance;
